"use client";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { IconButton } from "@ecco/ui/icon-button";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { Flex } from "@ecco/ui/flex";
import { PauseIcon, PlayIcon } from "@ecco/ui/carousel-icons";
import React from "react";
import {
    getControlButtonFocusStyles,
    getControlsContainerProps,
    outsideControlButtonProps,
    overlayControlButtonProps,
} from "./HeroVideoControlsStyle";

type HeroVideoControlsAppearance = "outside" | "overlay";

interface videoControlProps {
    isVideoMuted: boolean;
    toggleVideoMute: () => void;
    isVideoPlaying: boolean;
    setVideoPlaying: (playing: boolean, fromUser?: boolean) => void;
    isCarousel: boolean;
    videoPlayerId: string;
    appearance?: HeroVideoControlsAppearance;
}

export const HeroVideoControls = ({
    isVideoMuted,
    toggleVideoMute,
    isVideoPlaying,
    setVideoPlaying,
    isCarousel,
    videoPlayerId,
    appearance = "overlay",
}: videoControlProps) => {
    const { getMultiple } = useMicrocopy();
    const isOutside = appearance === "outside";
    const controlButtonProps = isOutside ? outsideControlButtonProps : overlayControlButtonProps;
    const focusVisibleStyles = getControlButtonFocusStyles(isOutside);
    const containerProps = getControlsContainerProps(appearance, isCarousel);

    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.video.ariaPlay,
        CommonCMS.video.ariaPause,
        CommonCMS.video.ariaMute,
        CommonCMS.video.ariaUnmute,
    ]);

    const playPauseIcon = isVideoPlaying ? (
        <PauseIcon width="20px" height="20px" aria-hidden="true" focusable="false" />
    ) : (
        <PlayIcon width="20px" height="20px" aria-hidden="true" focusable="false" />
    );

    return (
        <Flex {...containerProps}>
            <IconButton
                {...controlButtonProps}
                {...focusVisibleStyles}
                type="button"
                aria-controls={videoPlayerId}
                aria-pressed={isVideoMuted}
                aria-label={
                    isVideoMuted
                        ? microcopies[CommonCMS.video.ariaUnmute]
                        : microcopies[CommonCMS.video.ariaMute]
                }
                onClick={toggleVideoMute}
                icon={
                    <ECCOIcon
                        size={isOutside ? "sm" : "3xs"}
                        name={isVideoMuted ? "mute" : "volume"}
                    />
                }
            />
            {!isCarousel && (
                <IconButton
                    {...controlButtonProps}
                    {...focusVisibleStyles}
                    type="button"
                    aria-controls={videoPlayerId}
                    aria-pressed={isVideoPlaying}
                    aria-label={
                        isVideoPlaying
                            ? microcopies[CommonCMS.video.ariaPause]
                            : microcopies[CommonCMS.video.ariaPlay]
                    }
                    onClick={() => setVideoPlaying(!isVideoPlaying, true)}
                    icon={playPauseIcon}
                />
            )}
        </Flex>
    );
};

export default HeroVideoControls;
